import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../quantified-tracking/components/ResponsiveModal';
import { Activity } from '../../../../types/Activity';
import axios from 'axios';
import { activityService } from '../../../../services/activityService';
import standingTaskService from '../../../../services/standingTaskService';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';

interface QuantifiedTaskModalProps {
  open: boolean;
  task?: {
    id?: number;
    taskNumber?: string;
    name: string;
    activityId?: number;
    description?: string;
  } | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const QuantifiedTaskModal: React.FC<QuantifiedTaskModalProps> = ({
  open,
  task,
  onCancel,
  onSuccess
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    activityId: '',
    description: '',
    status: 'ACTIVE'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Load activities first, then set form data
      const initializeModal = async () => {
        setActivitiesLoading(true);
        try {
          await loadActivities(task);
        } finally {
          setActivitiesLoading(false);
        }
      };
      
      initializeModal();
    }
  }, [open, task]);

  // Set form data after activities are loaded
  useEffect(() => {
    if (open && activities.length >= 0) { // Allow setting form data even if no activities loaded yet
      if (task) {
        // Handle activityId: convert to string, but use empty string if 0 or undefined
        const activityIdValue = task.activityId && task.activityId !== 0 
          ? task.activityId.toString() 
          : '';
        
        // Verify the activity exists in the loaded activities
        const activityExists = activityIdValue ? activities.some(a => a.id.toString() === activityIdValue) : false;
        console.log('QuantifiedTaskModal: Setting form data', {
          taskActivityId: task.activityId,
          activityIdValue,
          activityExists,
          activitiesCount: activities.length,
          activityIds: activities.map(a => a.id)
        });
        
        setFormData({
          id: task.id?.toString?.() || '',
          name: task.name || '',
          activityId: activityExists ? activityIdValue : '',
          description: task.description || '',
          status: 'ACTIVE'
        });
      } else {
        setFormData({
          id: '',
          name: '',
          activityId: '',
          description: '',
          status: 'ACTIVE'
        });
      }
    }
  }, [open, task, activities]);

  const loadActivities = async (currentTask?: typeof task) => {
    try {
      // Load quantified activities and task activity in parallel for better performance
      const [quantifiedData, taskActivity] = await Promise.all([
        activityService.getActivitiesByClassification('quantified'),
        // Only fetch the specific task activity if we have a task and activityId
        currentTask?.activityId 
          ? activityService.getActivityById(currentTask.activityId).catch(() => null)
          : Promise.resolve(null)
      ]);

      const uniqueActivities = quantifiedData.reduce((acc: Activity[], current: Activity) => {
        const exists = acc.find(item => item.activityNumber === current.activityNumber);
        if (!exists) acc.push(current);
        return acc;
      }, []);
      
      // If we fetched the task activity and it's not in the filtered list, add it
      if (taskActivity) {
        const activityExists = uniqueActivities.some(a => a.id === taskActivity.id);
        if (!activityExists) {
          console.log('QuantifiedTaskModal: Adding task activity to list', {
            activityId: taskActivity.id,
            activityName: taskActivity.activityName
          });
          uniqueActivities.push(taskActivity);
        }
      }
      
      console.log('QuantifiedTaskModal: Loaded activities', {
        count: uniqueActivities.length,
        taskActivityId: currentTask?.activityId
      });
      setActivities(uniqueActivities);
    } catch (error) {
      console.error('Failed to load activities:', error);
      setError('Failed to load activities. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = {
        id: formData.id ? Number(formData.id) : undefined,
        name: formData.name,
        activityId: parseInt(formData.activityId),
        description: formData.description,
        type: 'Quantified' as const,
        status: formData.status
      };

      if (task?.id) {
        await standingTaskService.updateQuantifiedTask(task.id, data);
      } else {
        await standingTaskService.createQuantifiedTask(data);
      }

      onSuccess();
    } catch (error) {
      console.error('Failed to save task:', error);
      setError('Failed to save task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onCancel}
      title={task ? 'Edit Quantified Task' : 'Create Quantified Task'}
      footer={
        <div className="tw-flex tw-gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={(e) => handleSubmit(e as any)} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="tw-mb-3">
          <Form.Label>Task ID (optional)</Form.Label>
          <Form.Control
            type="number"
            value={formData.id}
            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
            disabled={!!task}
          />
          <Form.Text>Task Number: {formData.id ? `Q${formData.id}` : (task?.taskNumber || '(auto-generated on save)')}</Form.Text>
        </div>

        <div className="tw-mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter task name"
            required
          />
        </div>

        <div className="tw-mb-3">
          {activitiesLoading ? (
            <div>
              <Form.Label>
                Activity <span className="tw-text-danger">*</span>
              </Form.Label>
              <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface" style={{ padding: '0.375rem 0.75rem' }}>
                <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
                Loading activities...
              </div>
            </div>
          ) : (
            <SearchableDropdown
              label="Activity"
              options={activities}
              value={formData.activityId}
              onChange={(value) => setFormData({ ...formData, activityId: value })}
              formatOption={(activity) => `${activity.activityName} (${activity.activityNumber})`}
              getValue={(activity) => activity.id.toString()}
              placeholder="Select an activity"
              noOptionsMessage="No activities available"
              isRequired
            />
          )}
        </div>

        <div className="tw-mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter task description"
          />
        </div>

        <div className="tw-mb-3">
          <SearchableDropdown
            label="Status"
            options={['ACTIVE', 'PLANNED', 'CANCELLED']}
            value={formData.status}
            onChange={(value) => setFormData({ ...formData, status: value })}
            formatOption={(status) => status}
            getValue={(status) => status}
            placeholder="Select Status"
            noOptionsMessage="No statuses available"
          />
        </div>
      </Form>
    </ResponsiveModal>
  );
};

export default QuantifiedTaskModal; 